/*
 * Contract shared between the two runtime contexts of the widget:
 *
 *   - Host shell  (runs in the customer page, defines <constell-widget>, owns the iframe)
 *   - Framed app  (runs inside the transparent iframe, the real Vue widget)
 *
 * The widget is embedded as a cross-origin iframe so that NO host-page script
 * (analytics/pixel trackers, focus traps, form enhancers, Elementor, …) can reach
 * into the widget's DOM, its CSS, or its events. Everything the host page used to
 * touch directly - the `window.CONSTELL` API and the analytics events on `window`/
 * `dataLayer` - is bridged across the frame boundary with `postMessage` so the public
 * behaviour is byte-for-byte identical to the previous Shadow-DOM-only implementation.
 */
import { type WidgetTrackingDetailTyping, type WidgetTrackingEventNameTyping } from '@shared/typings';

// Marker set on the iframe's own window (by frame.html) before the bundle loads,
// so the single shared bundle knows which context it is booting in.
export const CONSTELL_FRAME_FLAG = '__CONSTELL_FRAME__';

// Every message carries this tag so unrelated postMessage traffic is ignored.
export const BRIDGE_SOURCE = 'constell-widget';

// Messages sent host -> frame.
export const HOST_TO_FRAME = {
    COMMAND: 'constell:command', // drive the CONSTELL API (open/close/toggle/navigate)
    CONFIG: 'constell:config', // forward a reactive attribute change
} as const;

// Messages sent frame -> host.
export const FRAME_TO_HOST = {
    READY: 'constell:ready', // frame booted and listening
    RESIZE: 'constell:resize', // desired iframe geometry changed
    TRACK: 'constell:track', // re-emit an analytics event on the host window
    OPEN_RESERVATION: 'constell:open-reservation', // open a 3rd-party provider on the host page
} as const;

export interface BridgeCommandTyping {
    command: 'open' | 'close' | 'toggle' | 'navigate';
    target?: string;
    payload?: Record<string, unknown>;
}

/*
 * Launcher box in frame-viewport coordinates. The iframe always spans the full viewport
 * (so CSS inside the frame sees the same viewport as the host page - see host-shell), and
 * while closed the host clips it to this box so the rest of the page stays clickable.
 * Ignored while open, when the frame owns the whole viewport.
 */
export interface BridgeResizeTyping {
    open: boolean;
    top: number;
    left: number;
    width: number;
    height: number;
}

export interface BridgeTrackTyping {
    name: WidgetTrackingEventNameTyping;
    detail: WidgetTrackingDetailTyping;
}

export interface BridgeReservationTyping {
    partnerId: number;
}

export interface BridgeMessageTyping {
    source: typeof BRIDGE_SOURCE;
    type: string;
    payload?: unknown;
}

// True when the current bundle is executing inside the widget iframe.
export const isFramedContext = (): boolean =>
    Boolean((window as unknown as Record<string, unknown>)[CONSTELL_FRAME_FLAG]);

// Wrap a payload in the tagged envelope both sides expect.
export const buildMessage = (type: string, payload?: unknown): BridgeMessageTyping => ({
    source: BRIDGE_SOURCE,
    type,
    payload,
});

// Narrow an incoming MessageEvent to a trusted bridge message (tag + expected origin).
export const readBridgeMessage = (event: MessageEvent, expectedOrigin?: string): BridgeMessageTyping | null => {
    if (expectedOrigin && event.origin !== expectedOrigin) return null;

    const raw = event.data as Partial<BridgeMessageTyping> | null;
    if (!raw || raw.source !== BRIDGE_SOURCE || typeof raw.type !== 'string') return null;

    return raw as BridgeMessageTyping;
};
